import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errMsg, todayIso } from "@/lib/api";

const COLORS = { attesa: "bg-amber-200 text-amber-950", compilato: "bg-sky-200 text-sky-950", confermato: "bg-emerald-300 text-emerald-950" };
const addDays = (iso, n) => {
  const d = new Date(iso + "T12:00:00");
  d.setDate(d.getDate() + n);
  return d.toLocaleDateString("sv-SE");
};

export default function CalendarPage() {
  const [start, setStart] = useState(todayIso().slice(0, 8) + "01");
  const [data, setData] = useState(null);
  const nav = useNavigate();
  const n = new Date(+start.slice(0, 4), +start.slice(5, 7), 0).getDate();
  useEffect(() => {
    api.get("/calendar", { params: { start, days: n } }).then(({ data }) => setData(data)).catch((e) => toast.error(errMsg(e)));
  }, [start, n]);
  const days = Array.from({ length: n }, (_, i) => addDays(start, i));
  const shift = (m) => {
    const d = new Date(+start.slice(0, 4), +start.slice(5, 7) - 1 + m, 1);
    setStart(d.toLocaleDateString("sv-SE").slice(0, 8) + "01");
  };
  const label = new Date(start + "T12:00:00").toLocaleDateString("it-IT", { month: "long", year: "numeric" });
  const today = todayIso();
  return (
    <div className="space-y-6" data-testid="calendar-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight">Calendario</h1>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => shift(-1)} data-testid="calendar-prev-button"><ChevronLeft className="h-4 w-4" /></Button>
          <span className="min-w-[150px] text-center font-semibold capitalize" data-testid="calendar-month-label">{label}</span>
          <Button variant="outline" size="icon" onClick={() => shift(1)} data-testid="calendar-next-button"><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>
      <div className="flex flex-wrap gap-3 text-xs">
        <span className="rounded px-2 py-1 bg-amber-200">In attesa dati</span>
        <span className="rounded px-2 py-1 bg-sky-200">Da confermare</span>
        <span className="rounded px-2 py-1 bg-emerald-300">Confermato</span>
        <span className="rounded px-2 py-1 bg-stone-300">Chiuso</span>
      </div>
      {data && (
        <div className="overflow-x-auto rounded-2xl border bg-white">
          <div className="grid min-w-max" style={{ gridTemplateColumns: `140px repeat(${n}, 40px)` }}>
            <div className="sticky left-0 z-10 bg-white border-b border-r p-2 text-xs font-semibold">Camera</div>
            {days.map((d) => (
              <div key={d} className={`border-b p-1 text-center text-[11px] ${d === today ? "bg-primary text-white" : data.closures.includes(d) ? "bg-stone-200" : ""}`}>
                <div className="font-bold">{+d.slice(8)}</div>
                <div className="opacity-70">{new Date(d + "T12:00:00").toLocaleDateString("it-IT", { weekday: "narrow" })}</div>
              </div>
            ))}
            {data.rooms.map((r) => (
              <div key={r.id} className="contents">
                <div className="sticky left-0 z-10 bg-white border-r border-b p-2 text-sm font-medium truncate" data-testid={`calendar-room-${r.id}`}>{r.name}</div>
                <div className="relative border-b h-12" style={{ gridColumn: `span ${n}` }}>
                  {data.bookings.filter((b) => b.room_id === r.id).map((b) => {
                    const s = Math.max(0, days.indexOf(b.arrival) === -1 ? (b.arrival < start ? 0 : n) : days.indexOf(b.arrival));
                    const endIdx = days.indexOf(b.departure) === -1 ? n : days.indexOf(b.departure);
                    const left = s * 40 + (b.arrival >= start ? 20 : 0);
                    const width = endIdx * 40 + (b.departure <= days[n - 1] ? 20 : 0) - left;
                    return (
                      <button key={b.id} onClick={() => nav(`/prenotazioni/${b.id}`)} data-testid={`calendar-booking-${b.id}`}
                        className={`absolute top-2 h-8 rounded-md px-2 text-left text-xs font-semibold truncate ${COLORS[b.status] || "bg-stone-200"}`}
                        style={{ left, width: Math.max(width, 16) }} title={`${b.ref_name} ${b.arrival} → ${b.departure}`}>
                        {b.ref_name}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
